import { Lender } from '@1delta/lender-registry'
import { formatUnits } from 'viem'
import { ProxyOracleAbi } from '../../../abis/oracle/ProxyOracle'
import {
  ListaMarketOverride,
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'

/** Lista oracle returns prices in 8 decimals (like Chainlink) */
const LISTA_DECIMALS = 8

/**
 * Market overrides for extending Lista markets beyond JSON data
 */
export type ListaMarketOverrides = {
  [chainId: string]: ListaMarketOverride[]
}

/**
 * Context for Lista fetcher with optional market overrides
 */
export interface ListaFetcherContext {
  marketOverrides?: ListaMarketOverrides
}

/**
 * Internal market structure for Lista
 */
interface ListaMarket {
  oracle: string
  collateralAsset: string
  loanAsset: string
  marketId: string
}

/**
 * Grouped metadata for deduplicated oracle calls
 * Deduplicates at the (oracle, asset) level since peek(asset) returns the same result
 */
interface ListaGroupedMeta {
  /** Map from "oracleLc:assetLc" key to index in the calls array */
  callIndexMap: { [key: string]: number }
  /** All markets to process */
  markets: ListaMarket[]
}

/**
 * Generate a unique market ID from oracle, loan asset, and collateral asset
 * Multiple markets can share the same oracle, so we include all three addresses
 * Result is a continuous hex string (oracle 40 chars + loan 40 chars + collateral 40 chars)
 */
function generateMarketId(oracle: string, loanAsset: string, collateralAsset: string): string {
  return `${oracle}${loanAsset}${collateralAsset}`.replace(/0x/gi, '').toUpperCase()
}

/**
 * Generate a lender key for a Lista market
 * Format: LISTA_DAO_${MARKET_ID}
 */
function generateListaLenderKey(marketId: string): string {
  return `${Lender.LISTA_DAO}_${marketId}`
}

/**
 * Get Lista markets for a chain.
 * Markets are sourced exclusively from overrides (the database).
 *
 * @param chainId - Chain ID to get markets for
 * @param marketOverrides - Markets from the database with proper market IDs
 */
function getListaMarketsForChain(
  chainId: string,
  marketOverrides?: ListaMarketOverrides,
): ListaMarket[] {
  const chainOverrides = marketOverrides?.[chainId] ?? []

  return chainOverrides.map((override) => ({
    oracle: override.oracle,
    collateralAsset: override.collateralAsset,
    loanAsset: override.loanAsset,
    marketId: override.marketId ?? generateMarketId(override.oracle, override.loanAsset, override.collateralAsset),
  }))
}

/**
 * Get multicall calls for Lista oracles
 * Deduplicates peek(asset) calls at the (oracle, asset) level.
 * Returns a single FetcherResult with all unique calls and market mappings.
 *
 * @param chainId - Chain ID to get calls for
 * @param context - Optional context with market overrides
 */
export function getListaCalls(
  chainId: string,
  context?: ListaFetcherContext,
): FetcherResult<ListaGroupedMeta>[] {
  const markets = getListaMarketsForChain(chainId, context?.marketOverrides)

  if (markets.length === 0) return []

  // Deduplicate calls at the (oracle, asset) level
  const calls: MulticallCall[] = []
  const callIndexMap: { [key: string]: number } = {}

  for (const market of markets) {
    const oracleLc = market.oracle.toLowerCase()

    // Collateral asset call
    const collateralKey = `${oracleLc}:${market.collateralAsset.toLowerCase()}`
    if (!(collateralKey in callIndexMap)) {
      callIndexMap[collateralKey] = calls.length
      calls.push({
        address: market.oracle,
        name: 'peek',
        params: [market.collateralAsset],
      })
    }

    // Loan asset call
    const loanKey = `${oracleLc}:${market.loanAsset.toLowerCase()}`
    if (!(loanKey in callIndexMap)) {
      callIndexMap[loanKey] = calls.length
      calls.push({
        address: market.oracle,
        name: 'peek',
        params: [market.loanAsset],
      })
    }
  }

  return [{
    calls,
    meta: { callIndexMap, markets },
    lender: generateListaLenderKey(markets[0].marketId),
  }]
}

/**
 * Parse Lista oracle results into structured price entries
 *
 * Processes all markets using the deduplicated call results.
 * Each entry gets a targetLender so it routes to the correct per-market lender key.
 *
 * Lista oracles return USD prices directly via peek(asset)
 * Prices are in 8 decimals (same as Chainlink)
 */
export function parseListaResults(
  data: any[],
  meta: ListaGroupedMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const entries: OraclePriceEntry[] = []

  for (const market of meta.markets) {
    const oracleLc = market.oracle.toLowerCase()
    const lenderKey = generateListaLenderKey(market.marketId)
    const processedAssets = new Set<string>()

    // Process both collateral and loan assets
    const assets = [market.collateralAsset, market.loanAsset]
    for (const assetRaw of assets) {
      const asset = assetRaw.toLowerCase()

      // Skip if already processed within this market
      if (processedAssets.has(asset)) continue

      const callKey = `${oracleLc}:${asset}`
      const callIndex = meta.callIndexMap[callKey]
      if (callIndex === undefined) continue

      const rawPrice = data[callIndex]
      if (rawPrice === undefined || rawPrice === '0x') continue

      try {
        const priceUSD = Number(formatUnits(BigInt(rawPrice.toString()), LISTA_DECIMALS))

        if (isNaN(priceUSD) || priceUSD === 0) continue

        entries.push({
          asset,
          price: priceUSD, // Raw price is already in USD
          priceUSD,
          marketUid: createMarketUid(context.chainId, lenderKey, asset),
          targetLender: lenderKey,
        })
        processedAssets.add(asset)
      } catch {
        // Skip invalid price data
      }
    }
  }

  return entries
}

/**
 * Get ABI for Lista oracle calls
 */
export function getListaAbi() {
  return ProxyOracleAbi
}

/**
 * Lista Oracle Fetcher module
 */
export const listaFetcher = {
  getCalls: getListaCalls,
  parse: parseListaResults,
  getAbi: getListaAbi,
}
